import React, { useState, useEffect, useMemo, useContext } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import Popup from "./popup";
import Input from "./input";

import { sortListByLocale } from "../../util/general.util";
import { DownArrowIcon, CheckedIcon } from "../../assets/icons";
import { WorkshopContext } from "../../contexts/workshop.context";
import { defaultPlatforms } from "../../util/workshop.util";

const MultiSelectionPicker = ({
  socket,
  moderator,
  userId,
  updateId,
  submitAllTogether = false,
  disableSelection = false,
  title,
  options = [],
  maxOptions = 3,
  saveWithIndex = false,
  alphabeticalOrder = true,
  enableSearchOption = false,
  dummySelections = [], // This is only to show dummy selections
  updateLocalSelection, // This passes selections to parent for dummy selections list,
  onChange,
  customClasses = "",
  textClasses = "text-gray-300",
  hideSelection = false,
  selectionTitle = ""
}) => {
  const { i18n, t } = useTranslation();

  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [alphabetizedList, setAlphabetizedList] = useState([]);
  const [disableInput, setDisableInput] = useState(disableSelection);

  const { workshopDetails: { platform } = {} } = useContext(WorkshopContext);

  const sortedList = (unsortedList) => {
    return sortListByLocale([...unsortedList], i18n.language);
  };

  const findCorrectIndex = (option) => {
    if (alphabeticalOrder) {
      const sortedListTemp = sortedList(options);
      return sortedListTemp.findIndex((o) => o === option);
    }

    return options.findIndex((o) => o === option);
  };

  const sortedOptions = useMemo(() => {
    if (alphabeticalOrder) {
      return sortedList(options);
    }
    return options;
  }, [options, alphabeticalOrder, i18n.language]);

  const selectOption = (option) => {
    if (moderator || disableSelection) return;

    const exists = selected.find((s) => s.index === option.index);

    if (maxOptions === 1 && !exists) {
      /** Act as a radio button */

      if (selected.length) {
        const [currentSelection] = selected;
        socket?.emit(updateId, {
          title: saveWithIndex ? findCorrectIndex(currentSelection.title) : currentSelection.title,
          score: -1
        });
      }

      setSelected([option]);

      socket?.emit(updateId, {
        userId: userId,
        title: saveWithIndex ? findCorrectIndex(option.title) : option.title,
        score: 1
      });

      if (!submitAllTogether) {
        setOpen(false);
        if (onChange) onChange([option.title]);
      }
      return;
    }

    if (selected.length === maxOptions && !exists) {
      if (!submitAllTogether) setOpen(false);
      return;
    }

    let newSelected;
    if (exists) {
      newSelected = selected.filter((s) => s.index !== option.index);
    } else {
      newSelected = [...selected, option];
    }

    setSelected(newSelected);

    if (!submitAllTogether) {
      const selectedTitle = saveWithIndex ? findCorrectIndex(option.title) : option.title;
      socket?.emit(updateId, {
        title: selectedTitle,
        score: exists ? -1 : 1
      });
      if (onChange) onChange([selectedTitle]);
    }
  };

  const submitAllOptions = () => {
    if (selected?.length === maxOptions) {
      const selectedTitles = selected.map((s) =>
        saveWithIndex ? findCorrectIndex(s.title) : s.title
      );

      socket?.emit(updateId, {
        userId: userId,
        titles: selectedTitles,
        score: 1
      });
      setOpen(false);
      setDisableInput(true);
      if (onChange) onChange(selectedTitles);
    } else {
      toast.info(t("error.selectAllOptions"));
    }
  };

  useEffect(() => {
    if (searchQuery === "") {
      setAlphabetizedList(sortedOptions);
    } else {
      const filteredOptions = sortedOptions.filter((option) =>
        option.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setAlphabetizedList(filteredOptions);
    }
  }, [searchQuery, sortedOptions]);

  useEffect(() => {
    if (updateLocalSelection) {
      updateLocalSelection(selected);
    }
  }, [selected]);

  useEffect(() => {
    setDisableInput(disableSelection);
  }, [disableSelection]);

  const isOnlineWorkshop =
    platform === defaultPlatforms.mtOnlineDemo || platform === defaultPlatforms.mtOnline;

  return (
    <>
      <div
        className={`p-4 flex flex-row gap-3 items-center justify-between cursor-pointer bg-white-100 rounded-lg border border-black-100 ${customClasses} `}
        onClick={() => {
          if (disableInput) return;

          setOpen(!open);
        }}>
        {selected?.length < maxOptions && !dummySelections?.length && (
          <>
            <span
              className={`text-md font-normal ${textClasses}`}
              dangerouslySetInnerHTML={{ __html: title }}
            />
            {!hideSelection && (
              <span className="w-4 h-4">
                <DownArrowIcon />
              </span>
            )}
          </>
        )}

        {(selected?.length === maxOptions || dummySelections?.length > 0) && (
          <div className="div">
            <span
              className={`block text-sm font-normal ${textClasses}`}
              dangerouslySetInnerHTML={{ __html: title }}
            />
            <div className="flex flex-row w-full gap-3 flex-wrap">
              {!isOnlineWorkshop &&
                alphabetizedList?.length > 0 &&
                !hideSelection &&
                dummySelections.map((option, index) => {
                  return (
                    <span
                      key={index}
                      className={`text-lg font-normal ${textClasses}`}
                      dangerouslySetInnerHTML={{
                        __html: `${!saveWithIndex ? option?.title || option : alphabetizedList?.[option]?.title || alphabetizedList?.[option]}${index + 1 < maxOptions ? "," : ""}`
                      }}
                    />
                  );
                })}
              {!isOnlineWorkshop &&
                !dummySelections?.length > 0 &&
                !hideSelection &&
                selected.map((option, index) => (
                  <span
                    key={index}
                    className={`text-lg font-normal ${textClasses}`}
                    dangerouslySetInnerHTML={{
                      __html: `${option?.title}${index + 1 < maxOptions ? "," : ""}`
                    }}
                  />
                ))}
            </div>
          </div>
        )}
      </div>
      {isOnlineWorkshop && !hideSelection && selected.length > 0 && (
        <div className="flex flex-col justify-between cursor-pointer bg-orange-50 rounded-2xl border border-black-100 mt-2 px-6 py-2">
          <p
            className="text-orange-default font-bold text-base mb-1"
            dangerouslySetInnerHTML={{ __html: selectionTitle }}
          />
          <ul className="list-inside list-disc">
            {selected.map((option, index) => (
              <li
                key={index}
                className={`text-xs font-normal`}
                dangerouslySetInnerHTML={{
                  __html: `${option?.title}`
                }}
              />
            ))}
          </ul>
        </div>
      )}
      <Popup
        open={open}
        setOpen={setOpen}
        headerButton={{
          btnText: t("popupSubmit"),
          callback: submitAllTogether ? submitAllOptions : null
        }}>
        {enableSearchOption && (
          <div className="px-4">
            <Input
              onChange={setSearchQuery}
              placeholder={t("searchFieldPlaceholder")}
              className="!bg-white-100 border !border-solid border-gray-200"
            />
          </div>
        )}
        <div className="flex flex-col gap-4 p-4">
          {alphabetizedList.map((option, index) => (
            <Option
              key={index}
              index={index}
              title={option}
              selected={selected.find(
                (selectedOption) =>
                  selectedOption?.title?.toLowerCase()?.trim() === option?.toLowerCase()?.trim()
              )}
              onClick={selectOption}
            />
          ))}
        </div>
      </Popup>
    </>
  );
};

const Option = ({ index, title, selected, onClick }) => {
  return (
    <div
      className={`grid grid-cols-[1fr_30px] cursor-pointer`}
      onClick={() => onClick({ index, title })}>
      <span
        className="text-lg font-normal text-gray-300"
        dangerouslySetInnerHTML={{ __html: title }}
      />
      <span className="w-6 h-6 justify-self-end">
        <CheckedIcon bg={selected ? "#E59536" : "none"} />
      </span>
    </div>
  );
};

export default MultiSelectionPicker;
